import { useEffect, useRef } from 'react';

/**
 * Signs the user out after a period with no keyboard, mouse or touch input
 * (VAPT 2026-10-16, finding 7).
 *
 * The BFF also limits sessions server-side (15 min access token, 30 min idle
 * refresh, 8 h absolute), but background polling and sockets keep a tab's
 * token fresh even when nobody is at the desk. Only the page can tell that no
 * person is there.
 *
 * The last-activity time is shared through localStorage, so input in any tab
 * keeps every tab of this browser signed in.
 */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const LAST_ACTIVITY_KEY = 'lastActivityAt';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;
// Don't write localStorage on every mousemove.
const WRITE_THROTTLE_MS = 15 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;

function readLastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeLastActivity(at: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Storage blocked: this tab still tracks activity in memory.
  }
}

interface Options {
  /** Only track while someone is signed in. */
  enabled: boolean;
  /** Pause while idle input is expected, e.g. during a live call. */
  paused?: boolean;
  onIdle: () => void;
  timeoutMs?: number;
}

export function useIdleLogout({ enabled, paused = false, onIdle, timeoutMs = IDLE_TIMEOUT_MS }: Options): void {
  const lastActivity = useRef(Date.now());
  const lastWrite = useRef(0);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    const markActive = () => {
      const now = Date.now();
      lastActivity.current = now;
      if (now - lastWrite.current >= WRITE_THROTTLE_MS) {
        lastWrite.current = now;
        writeLastActivity(now);
      }
    };
    // Signing in counts as activity, so a stale value from an old session
    // can't end this one at once.
    lastWrite.current = 0;
    markActive();

    let fired = false;
    const check = () => {
      if (fired) return;
      const shared = readLastActivity();
      const last = Math.max(lastActivity.current, shared ?? 0);
      if (Date.now() - last >= timeoutMs) {
        fired = true;
        onIdleRef.current();
      }
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    // A tab coming back from sleep or the background checks straight away.
    document.addEventListener('visibilitychange', check);
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener('visibilitychange', check);
      window.clearInterval(timer);
    };
  }, [enabled, timeoutMs]);

  // While paused (e.g. on a call), keep the clock fresh so the countdown
  // restarts from the end of the pause rather than firing at once.
  useEffect(() => {
    if (!enabled || !paused) return;
    const keepAlive = () => {
      lastActivity.current = Date.now();
      writeLastActivity(lastActivity.current);
    };
    keepAlive();
    const timer = window.setInterval(keepAlive, WRITE_THROTTLE_MS);
    return () => {
      window.clearInterval(timer);
      keepAlive();
    };
  }, [enabled, paused]);
}
